import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI } from '../services/api';
import { useWSEvent } from '../contexts/WebSocketContext';

export const isSafeInternalLink = (link) =>
  typeof link === 'string' && link.startsWith('/') && !link.startsWith('//');

export const NOTIF_TYPE_COLOR = {
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
  info: 'bg-blue-500',
};

/**
 * Shared inbox loader + WS prepend for NotificationBell / NotificationsPage.
 * @param {{ enabled?: boolean, pollMs?: number, trackUnread?: boolean }} opts
 */
export function useNotificationsInbox({ enabled = true, pollMs = 30000, trackUnread = true } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const openRef = useRef(false);

  const setOpenRef = useCallback((open) => { openRef.current = !!open; }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      if (trackUnread) {
        const [notifs, countData] = await Promise.all([
          notificationAPI.getAll(),
          notificationAPI.getUnreadCount(),
        ]);
        setNotifications(Array.isArray(notifs) ? notifs : []);
        setUnread(countData?.count ?? 0);
      } else {
        const data = await notificationAPI.getAll();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch {
      /* callers may toast */
      throw new Error('load_failed');
    } finally {
      setLoading(false);
    }
  }, [enabled, trackUnread]);

  useEffect(() => {
    if (!enabled) return undefined;
    load().catch(() => {});
    if (!pollMs) return undefined;
    const t = setInterval(() => { load().catch(() => {}); }, pollMs);
    return () => clearInterval(t);
  }, [enabled, load, pollMs]);

  useWSEvent('notification_new', useCallback((data) => {
    setNotifications((prev) => [data, ...prev]);
    if (trackUnread && !openRef.current) setUnread((p) => p + 1);
  }, [trackUnread]));

  const markAllRead = useCallback(async () => {
    await notificationAPI.markAllRead();
    await load();
  }, [load]);

  const markRead = useCallback(async (id) => {
    await notificationAPI.markRead(id);
    await load();
  }, [load]);

  const deleteOne = useCallback(async (id) => {
    await notificationAPI.deleteOne(id);
    await load();
  }, [load]);

  return {
    notifications,
    unread,
    loading,
    load,
    markAllRead,
    markRead,
    deleteOne,
    setOpenRef,
  };
}
